import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import type { ServiceabilityLocation } from "../types";

interface ServiceabilityNodeProps {
	level: number;
	node: ServiceabilityLocation;
	selectedIds: Set<number>;
	onToggle: (id: number, descendantIds: number[]) => void;
}

const getAllDescendantIds = (node: ServiceabilityLocation): number[] => {
	const ids = [node.id];
	node.children?.forEach((child) => {
		ids.push(...getAllDescendantIds(child));
	});
	return ids;
};

export const ServiceabilityNode: React.FC<ServiceabilityNodeProps> = ({
	node,
	selectedIds,
	onToggle,
	level,
}) => {
	const [expanded, setExpanded] = useState(true);
	const checkboxRef = useRef<HTMLInputElement>(null);
	const hasChildren = node.children?.length > 0;

	const descendantIds = useMemo(() => getAllDescendantIds(node), [node]);

	const isChecked = descendantIds.every((id) => selectedIds.has(id));
	const isIndeterminate =
		!isChecked && descendantIds.some((id) => selectedIds.has(id));

	useEffect(() => {
		if (checkboxRef.current) {
			checkboxRef.current.indeterminate = isIndeterminate;
		}
	}, [isIndeterminate]);

	const handleRowClick = () => {
		if (hasChildren && !expanded) {
			setExpanded(true);
		}
	};

	return (
		<div style={{ paddingLeft: level * 24 }}>
			<div
				className={`flex items-center gap-2 py-1 ${hasChildren && !expanded ? "cursor-pointer" : ""}`}
				onClick={handleRowClick}
			>
				{hasChildren ? (
					<button
						type="button"
						className="flex h-5 w-5 items-center justify-center text-gray-500 hover:text-gray-700"
						onClick={(e) => {
							e.stopPropagation();
							setExpanded(!expanded);
						}}
					>
						{expanded ? <FiChevronDown /> : <FiChevronRight />}
					</button>
				) : (
					<span className="inline-block h-5 w-5" />
				)}

				<input
					ref={checkboxRef}
					type="checkbox"
					checked={isChecked}
					onChange={() => onToggle(node.id, descendantIds)}
					onClick={(e) => e.stopPropagation()}
					className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
				/>

				<span
					className="cursor-pointer select-none text-sm text-gray-700"
					onClick={(e) => {
						e.stopPropagation();
						onToggle(node.id, descendantIds);
					}}
				>
					{node.name}
				</span>
			</div>

			{expanded &&
				node.children?.map((child) => (
					<ServiceabilityNode
						key={child.id}
						node={child}
						selectedIds={selectedIds}
						onToggle={onToggle}
						level={level + 1}
					/>
				))}
		</div>
	);
};
